/**
 * HouseResolver - Resolves student house name to houseId
 *
 * Students store house as a string name (e.g., "Blue House").
 * Houses are defined in schools/{schoolId}/houses with an ID.
 * This utility bridges the gap with caching.
 */

import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Cache: schoolId → { houses: Map<lowerName, {id, name, colour}>, ts }
const housesCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Get cached houses map for a school (name → house doc)
 */
async function getHousesMap(schoolId) {
  const cached = housesCache.get(schoolId);
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.houses;

  const snapshot = await getDocs(collection(db, 'schools', schoolId, 'houses'));
  const houses = new Map();
  snapshot.docs.forEach(d => {
    const data = d.data();
    const key = (data.name || '').toLowerCase().trim();
    if (key) houses.set(key, { id: d.id, name: data.name, colour: data.colour });
  });
  housesCache.set(schoolId, { houses, ts: Date.now() });
  return houses;
}

/**
 * Resolve a student's house name to a houseId
 * Fetches the student doc, reads their house name, matches against school houses.
 *
 * @param {string} studentId - Student document ID
 * @param {string} schoolId - School ID
 * @returns {Promise<{houseId: string|null, houseName: string|null}>}
 */
export async function resolveHouseForStudent(studentId, schoolId) {
  if (!studentId || !schoolId) return { houseId: null, houseName: null };

  try {
    const studentRef = doc(db, 'schools', schoolId, 'students', studentId);
    const studentSnap = await getDoc(studentRef);
    if (!studentSnap.exists()) return { houseId: null, houseName: null };

    const studentHouse = studentSnap.data().house;
    if (!studentHouse) return { houseId: null, houseName: null };

    const housesMap = await getHousesMap(schoolId);
    const match = housesMap.get(studentHouse.toLowerCase().trim());

    if (match) return { houseId: match.id, houseName: match.name };
    return { houseId: null, houseName: studentHouse };
  } catch (err) {
    console.warn('[HouseResolver] Failed to resolve house:', err.message);
    return { houseId: null, houseName: null };
  }
}

/**
 * Invalidate houses cache for a school (call after houses are modified)
 */
export function invalidateHousesCache(schoolId) {
  if (schoolId) housesCache.delete(schoolId);
  else housesCache.clear();
}
